import type { ThreadParticipant } from './api';

export type ChipRole = 'client' | 'admin' | 'member' | 'watcher';

interface RoleStyle { label: string; color: string; background: string; }

// Single source of truth for role coloring — replaces five independent
// copy-pasted maps (UsersTab, TeamTab, TopBar, MessageInput's mention
// popover, ChatHeader's participant chips), none of which handled a
// "client" role. Amber is intentionally avoided here for body-size text
// per the design doc's own contrast rule (raw --amber is for small
// dots/icons only) — client uses the --note-label step instead.
const ROLE_STYLES: Record<ChipRole, RoleStyle> = {
  client:  { label: 'Client',  color: 'var(--note-label)', background: 'rgba(239,159,39,.15)' },
  admin:   { label: 'Admin',   color: 'var(--red)',        background: 'rgba(226,75,74,.16)' },
  member:  { label: 'Member',  color: 'var(--blue-soft)',  background: 'rgba(55,138,221,.18)' },
  watcher: { label: 'Watcher', color: 'var(--v2)',         background: 'rgba(29,158,117,.18)' },
};

const FALLBACK_STYLE: RoleStyle = { label: 'Member', color: 'var(--shell-xs)', background: 'var(--hover-strong)' };

export function roleStyle(role: string | null | undefined): RoleStyle {
  if (!role) return FALLBACK_STYLE;
  const key = role.toLowerCase() as ChipRole;
  return ROLE_STYLES[key] ?? FALLBACK_STYLE;
}

/** WhatsApp contacts have no `role` field at all — they're identified by `contact_id`. */
export function participantRole(p: ThreadParticipant): ChipRole {
  if (p.contact_id) return 'client';
  const key = (p.role ?? 'member').toLowerCase();
  return (key === 'admin' || key === 'watcher' || key === 'client') ? key : 'member';
}
